import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { UserPlus, Download, FileSpreadsheet, Trash2, AlertTriangle, Loader2, BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  // LAO creation state
  const [laoForm, setLaoForm] = useState({ name: '', email: '', password: '' });
  const [laoLoading, setLaoLoading] = useState(false);

  // Cleanup state
  const [cleanupStep, setCleanupStep] = useState(0);
  const [confirmText, setConfirmText] = useState('');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const CONFIRM_PHRASE = 'DELETE ALL VIDEOS';

  const handleCleanupClick = () => setCleanupStep(1);
  
  const handleCleanupClose = () => {
    setCleanupStep(0);
    setConfirmText('');
  };

  const handleCreateLAO = async (e) => {
    e.preventDefault();
    if (!laoForm.name || !laoForm.email || !laoForm.password) {
      toast.error(t('fillAllFields') || 'Please fill all fields');
      return;
    }
    setLaoLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/create-lao`, {
        name: laoForm.name,
        email: laoForm.email,
        password: laoForm.password,
        role: 'lao'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('laoCreated') || 'LAO account created successfully');
      setLaoForm({ name: '', email: '', password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
    setLaoLoading(false);
  };

  const handleCleanupExecute = async () => {
    setCleanupLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/cleanup-all-videos`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(t('cleanupSuccess') || 'تم تنظيف جميع الفيديوهات بنجاح');
        const stats = response.data.stats;
        if (stats) {
          toast.info(`${stats.db_records_deleted} سجل | ${stats.files_deleted} ملف | ${stats.space_freed_mb} MB`);
        }
      } else {
        toast.error(t('cleanupFailed') || 'فشل التنظيف');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setCleanupLoading(false);
      handleCleanupClose();
    }
  };

  const handleAddTrainer = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/teacher/add-trainer`, formData);
      
      toast.success(t('success'));
      setFormData({ name: '', email: '', password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const downloadSubmissions = async (format = 'csv') => {
    try {
      if (format === 'pdf') {
        // Download PDF directly
        const response = await axios.get(`${API}/teacher/export/submissions?format=pdf`, {
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `exam_results_${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        
        toast.success('PDF downloaded successfully');
      } else {
        // CSV format
        const response = await axios.get(`${API}/teacher/export/submissions`);
        const data = response.data.data;
        
        if (data.length === 0) {
          toast.info('No submissions found');
          return;
        }

        // Convert to CSV
        const headers = ['Exam Title', 'Student Name', 'Student Email', 'Score', 'Total Questions', 'Submitted At'];
        const csvContent = [
          headers.join(','),
          ...data.map(row => [
            `"${row.exam_title}"`,
            `"${row.student_name}"`,
            `"${row.student_email}"`,
            row.score,
            row.total_questions,
            new Date(row.submitted_at).toLocaleString()
          ].join(','))
        ].join('\n');

        // Download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `exam_submissions_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        
        toast.success(`Downloaded ${data.length} submissions`);
      }
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const downloadPendingExams = async (format = 'csv') => {
    try {
      if (format === 'pdf') {
        // Download PDF directly
        const response = await axios.get(`${API}/teacher/export/pending?format=pdf`, {
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `pending_exams_${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        
        toast.success('PDF downloaded successfully');
      } else {
        // CSV format
        const response = await axios.get(`${API}/teacher/export/pending`);
        const data = response.data.data;
        
        if (data.length === 0) {
          toast.info('All students have completed their exams');
          return;
        }

        // Convert to CSV
        const headers = ['Exam Title', 'Student Name', 'Student Email', 'Status'];
        const csvContent = [
          headers.join(','),
          ...data.map(row => [
            `"${row.exam_title}"`,
            `"${row.student_name}"`,
            `"${row.student_email}"`,
            row.status
          ].join(','))
        ].join('\n');

        // Download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `pending_exams_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        
        toast.success(`Downloaded ${data.length} pending exams`);
      }
    } catch (error) {
      toast.error(t('error'));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-panel">
      <div>
        <h2 className="text-3xl font-bold text-gray-900">{t('adminPanel')}</h2>
        <p className="text-gray-600 mt-1">{t('manageTrainers')}</p>
      </div>

      {/* Export Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="shadow-lg border-0">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              {t('exportData')}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-700">{t('downloadSubmissions')}</p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  data-testid="download-submissions-pdf"
                  onClick={() => downloadSubmissions('pdf')}
                  className="bg-gradient-to-r from-red-600 to-pink-600"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('downloadPDF')}
                </Button>
                <Button
                  data-testid="download-submissions-csv"
                  onClick={() => downloadSubmissions('csv')}
                  className="bg-gradient-to-r from-green-600 to-emerald-600"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('downloadCSV')}
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-700">{t('downloadPendingExams')}</p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  data-testid="download-pending-pdf"
                  onClick={() => downloadPendingExams('pdf')}
                  variant="outline"
                  className="border-red-500 text-red-600 hover:bg-red-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('downloadPDF')}
                </Button>
                <Button
                  data-testid="download-pending-csv"
                  onClick={() => downloadPendingExams('csv')}
                  variant="outline"
                  className="border-green-500 text-green-600 hover:bg-green-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('downloadCSV')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add Trainer Form */}
        <Card className="shadow-lg border-0">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-cyan-50">
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              {t('addTrainer')}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleAddTrainer} className="space-y-4">
              <div className="space-y-2">
                <Label>{t('name')}</Label>
                <Input
                  data-testid="trainer-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>{t('email')}</Label>
                <Input
                  data-testid="trainer-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>{t('password')}</Label>
                <Input
                  data-testid="trainer-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                />
              </div>

              <Button
                data-testid="add-trainer-button"
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-600 to-cyan-600"
              >
                {loading ? t('loading') : t('addTrainer')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Admin Cleanup Section */}
      <Card className="shadow-lg border-0 border-l-4 border-l-red-500">
        <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50">
          <CardTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="h-5 w-5" />
            {t('systemCleanup') || 'تنظيف النظام (مدير فقط)'}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <p className="text-sm text-gray-600 mb-4">
            {t('cleanupWarningDesc') || 'حذف جميع ملفات الفيديو المخزنة. استخدم هذا قبل أو بعد النشر.'}
          </p>
          <Button
            data-testid="cleanup-all-videos-btn"
            onClick={handleCleanupClick}
            variant="destructive"
            className="bg-red-600 hover:bg-red-700"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t('cleanAllVideos') || '🧹 تنظيف جميع الفيديوهات'}
          </Button>
        </CardContent>
      </Card>

      {/* Create LAO Account Section */}
      <Card className="shadow-lg border-0 border-l-4 border-l-purple-500">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardTitle className="flex items-center gap-2 text-purple-700">
            <BarChart3 className="h-5 w-5" />
            {t('createLAO') || 'إنشاء حساب مسؤول تحليلات التعلم'}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <p className="text-sm text-gray-600 mb-4">
            {t('laoDescription') || 'Learning Analytics Officer - دور للمراقبة والتحليل وإعداد التقارير فقط'}
          </p>
          <form onSubmit={handleCreateLAO} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder={t('name') || 'الاسم'}
                value={laoForm.name}
                onChange={(e) => setLaoForm({...laoForm, name: e.target.value})}
                required
              />
              <Input
                type="email"
                placeholder={t('email') || 'البريد الإلكتروني'}
                value={laoForm.email}
                onChange={(e) => setLaoForm({...laoForm, email: e.target.value})}
                required
              />
              <Input
                type="password"
                placeholder={t('password') || 'كلمة المرور'}
                value={laoForm.password}
                onChange={(e) => setLaoForm({...laoForm, password: e.target.value})}
                required
              />
            </div>
            <Button
              type="submit"
              disabled={laoLoading}
              className="bg-gradient-to-r from-purple-600 to-indigo-600"
            >
              {laoLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BarChart3 className="h-4 w-4 mr-2" />}
              {t('createLAOAccount') || 'إنشاء حساب LAO'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Step 1: Warning Modal */}
      <Dialog open={cleanupStep === 1} onOpenChange={() => handleCleanupClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-6 w-6" />
              {t('warningTitle') || 'تحذير هام!'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-gray-700 font-medium">
              {t('cleanupWarning1') || 'هذا الإجراء سيحذف جميع ملفات الفيديو المخزنة نهائياً.'}
            </p>
            <p className="text-red-600 font-bold">
              {t('cleanupWarning2') || 'لا يمكن التراجع عن هذا الإجراء!'}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCleanupClose}>
              {t('cancel') || 'إلغاء'}
            </Button>
            <Button variant="destructive" onClick={() => setCleanupStep(2)}>
              {t('continue') || 'متابعة'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 2: Type Confirmation */}
      <Dialog open={cleanupStep === 2} onOpenChange={() => handleCleanupClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {t('confirmAction') || 'تأكيد الإجراء'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-gray-700">
              {t('typeToConfirm') || 'اكتب النص التالي للتأكيد:'}
            </p>
            <code className="block bg-gray-100 p-3 rounded text-center font-mono font-bold text-red-600">
              {CONFIRM_PHRASE}
            </code>
            <Input
              data-testid="cleanup-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              className="font-mono"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCleanupClose}>
              {t('cancel') || 'إلغاء'}
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => setCleanupStep(3)}
              disabled={confirmText !== CONFIRM_PHRASE}
              data-testid="cleanup-step2-confirm"
            >
              {t('confirm') || 'تأكيد'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 3: Final Confirmation */}
      <Dialog open={cleanupStep === 3} onOpenChange={() => handleCleanupClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-6 w-6" />
              {t('finalConfirmation') || 'التأكيد النهائي'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700 font-bold text-center text-lg">
              {t('areYouSure') || 'هل أنت متأكد تماماً؟'}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCleanupClose}>
              {t('no') || 'لا'}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCleanupExecute}
              disabled={cleanupLoading}
              data-testid="cleanup-final-confirm"
            >
              {cleanupLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('processing') || 'جاري التنفيذ...'}
                </>
              ) : (
                t('yesDelete') || 'نعم، احذف الكل'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
